import { Button, Chip, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  EmptyState,
  InfoCard,
  StatusOK,
  StatusPending,
} from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  EntityJenkinsContent,
  isJenkinsAvailable,
} from '@backstage/plugin-jenkins';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import { useEntity } from '@backstage/plugin-catalog-react';

// ─── Health Check Card ────────────────────────────────────────────────────────
// Lee la annotation wootic.com/health-check-url y muestra el endpoint como
// badge clickable. Para checkear el estado real, el usuario abre el endpoint.

const HEALTH_ANNOTATION = 'wootic.com/health-check-url';

const HealthCheckCard = () => {
  const { entity } = useEntity();
  const healthUrl = entity.metadata.annotations?.[HEALTH_ANNOTATION];

  return (
    <InfoCard title="Health Check" subheader="Endpoint de salud del servicio">
      <Grid container spacing={2} alignItems="center">
        {healthUrl ? (
          <>
            <Grid item xs={12}>
              <StatusOK>Endpoint configurado</StatusOK>
            </Grid>
            <Grid item xs={12}>
              <Chip
                label={healthUrl}
                size="small"
                clickable
                component="a"
                href={healthUrl}
                target="_blank"
                rel="noopener noreferrer"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                href={healthUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir endpoint
              </Button>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <StatusPending>Sin endpoint configurado</StatusPending>
            <p style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              Agregá la annotation <code>{HEALTH_ANNOTATION}</code> en tu{' '}
              <code>catalog-info.yaml</code>
            </p>
          </Grid>
        )}
      </Grid>
    </InfoCard>
  );
};

// ─── AWS Info Card ────────────────────────────────────────────────────────────
// Muestra las annotations de AWS (account, region, role) como chips.

const AWS_ACCOUNT_ANNOTATION = 'aws.amazon.com/aws-account-id';
const AWS_REGION_ANNOTATION = 'aws.amazon.com/aws-region';
const AWS_ROLE_ANNOTATION = 'aws.amazon.com/aws-assume-role-arn';

const AwsInfoCard = () => {
  const { entity } = useEntity();
  const accountId = entity.metadata.annotations?.[AWS_ACCOUNT_ANNOTATION];
  const region = entity.metadata.annotations?.[AWS_REGION_ANNOTATION];
  const roleArn = entity.metadata.annotations?.[AWS_ROLE_ANNOTATION];

  if (!accountId && !region) {
    return (
      <InfoCard title="AWS" subheader="Cuenta y región del recurso">
        <StatusPending>Sin configuración AWS</StatusPending>
        <p style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          Agregá <code>aws.amazon.com/aws-account-id</code> y{' '}
          <code>aws.amazon.com/aws-region</code>
        </p>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="AWS" subheader="Cuenta y región del recurso">
      <Grid container spacing={1}>
        {accountId && (
          <Grid item xs={12}>
            <Chip label={`Account: ${accountId}`} size="small" variant="outlined" />
          </Grid>
        )}
        {region && (
          <Grid item xs={12}>
            <Chip label={`Region: ${region}`} size="small" variant="outlined" />
          </Grid>
        )}
        {roleArn && (
          <Grid item xs={12}>
            <Chip
              label={`Role: ${roleArn.split('/').pop()}`}
              size="small"
              variant="outlined"
              title={roleArn}
            />
          </Grid>
        )}
        <Grid item xs={12} style={{ marginTop: 8 }}>
          <Button
            variant="outlined"
            size="small"
            href={`https://console.aws.amazon.com/home?region=${region ?? 'us-east-1'}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir AWS Console
          </Button>
        </Grid>
      </Grid>
    </InfoCard>
  );
};

// ─── CI/CD ────────────────────────────────────────────────────────────────────

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isJenkinsAvailable}>
      <EntityJenkinsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No hay CI/CD configurado para este componente"
        missing="info"
        description="Agregá jenkins.io/job-full-name (Jenkins) o github.com/project-slug (GitHub Actions) en el catalog-info.yaml."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
            target="_blank"
          >
            Ver annotations disponibles
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

// ─── Warnings ─────────────────────────────────────────────────────────────────

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}><EntityOrphanWarning /></Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}><EntityRelationWarning /></Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}><EntityProcessingErrorsPanel /></Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

// ─── Overview ─────────────────────────────────────────────────────────────────

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>
    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={4} xs={12}>
      <HealthCheckCard />
    </Grid>
    <Grid item md={4} xs={12}>
      <AwsInfoCard />
    </Grid>
    <Grid item md={12} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

// ─── TechDocs ─────────────────────────────────────────────────────────────────

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

// ─── Service entity page ──────────────────────────────────────────────────────

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">{overviewContent}</EntityLayout.Route>
    <EntityLayout.Route path="/ci-cd" title="CI/CD">{cicdContent}</EntityLayout.Route>
    <EntityLayout.Route path="/kubernetes" title="Kubernetes" if={isKubernetesAvailable}>
      <EntityKubernetesContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}><EntityProvidedApisCard /></Grid>
        <Grid item md={6}><EntityConsumedApisCard /></Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencias">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}><EntityDependsOnComponentsCard variant="gridItem" /></Grid>
        <Grid item md={6}><EntityDependsOnResourcesCard variant="gridItem" /></Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">{techdocsContent}</EntityLayout.Route>
  </EntityLayout>
);

// ─── Website entity page ──────────────────────────────────────────────────────

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">{overviewContent}</EntityLayout.Route>
    <EntityLayout.Route path="/ci-cd" title="CI/CD">{cicdContent}</EntityLayout.Route>
    <EntityLayout.Route path="/kubernetes" title="Kubernetes" if={isKubernetesAvailable}>
      <EntityKubernetesContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencias">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}><EntityDependsOnComponentsCard variant="gridItem" /></Grid>
        <Grid item md={6}><EntityDependsOnResourcesCard variant="gridItem" /></Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">{techdocsContent}</EntityLayout.Route>
  </EntityLayout>
);

// ─── Default entity page ──────────────────────────────────────────────────────

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">{overviewContent}</EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">{techdocsContent}</EntityLayout.Route>
  </EntityLayout>
);

// ─── Component page ───────────────────────────────────────────────────────────

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>{serviceEntityPage}</EntitySwitch.Case>
    <EntitySwitch.Case if={isComponentType('website')}>{websiteEntityPage}</EntitySwitch.Case>
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

// ─── API page ─────────────────────────────────────────────────────────────────

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}><EntityAboutCard /></Grid>
        <Grid item md={6} xs={12}><EntityCatalogGraphCard variant="gridItem" height={400} /></Grid>
        <Grid item md={4} xs={12}><EntityLinksCard /></Grid>
        <Grid container item md={12}>
          <Grid item md={6}><EntityProvidingComponentsCard /></Grid>
          <Grid item md={6}><EntityConsumingComponentsCard /></Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}><EntityApiDefinitionCard /></Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ─── User page ────────────────────────────────────────────────────────────────

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}><EntityUserProfileCard variant="gridItem" /></Grid>
        <Grid item xs={12} md={6}><EntityOwnershipCard variant="gridItem" /></Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ─── Group page ───────────────────────────────────────────────────────────────

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}><EntityGroupProfileCard variant="gridItem" /></Grid>
        <Grid item xs={12} md={6}><EntityOwnershipCard variant="gridItem" /></Grid>
        <Grid item xs={12} md={6}><EntityMembersListCard /></Grid>
        <Grid item xs={12} md={6}><EntityLinksCard /></Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ─── System page ──────────────────────────────────────────────────────────────

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}><EntityAboutCard variant="gridItem" /></Grid>
        <Grid item md={6} xs={12}><EntityCatalogGraphCard variant="gridItem" height={400} /></Grid>
        <Grid item md={4} xs={12}><EntityLinksCard /></Grid>
        <Grid item md={8}><EntityHasComponentsCard variant="gridItem" /></Grid>
        <Grid item md={6}><EntityHasApisCard variant="gridItem" /></Grid>
        <Grid item md={6}><EntityHasResourcesCard variant="gridItem" /></Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagrama">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="Diagrama del sistema"
        height={700}
        relations={[
          RELATION_PART_OF, RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY, RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API, RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF, RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

// ─── Domain page ──────────────────────────────────────────────────────────────

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}><EntityAboutCard variant="gridItem" /></Grid>
        <Grid item md={6} xs={12}><EntityCatalogGraphCard variant="gridItem" height={400} /></Grid>
        <Grid item md={6}><EntityHasSystemsCard variant="gridItem" /></Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ─── Root export ──────────────────────────────────────────────────────────────

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
